// Course category icons
import {FaRobot, FaCode, FaMicrochip, FaFlask, FaGamepad, FaChalkboardTeacher, FaLightbulb} from 'react-icons/fa';

// Courses data with full details
const coursesData = [
  {
    id: 'stem-crash-course-12sessions',
    title: '12-Session STEM Crash Course',
    shortDescription: 'An immersive, hands-on STEM program with coding, electronics, robotics, AI, and soft skill development.',
    category: 'STEM',
    categoryIcon: FaLightbulb, // Or FaCogs, FaBrain – choose based on icon library
    level: 'Beginner to Intermediate',
    duration: '2 weeks (12 sessions)',
    ageGroup: '10-14 years',
    image: '/courses/stem-crash-course-12.jpg', // Replace with actual image path
    price: 'LKR 7,500',
    availability: 'Available',
    rating: 4.95,
    studentsEnrolled: 120,
    fullDescription: `
    This specially designed 12-session STEM Crash Course introduces students to the exciting world of robotics, electronics, AI, and programming through hands-on activities and creative challenges.
    
    Students will explore STEM concepts using fun tools like Pictoblox or Scratch, simulate circuits online, build their own projects, and learn essential skills like problem-solving, communication, and teamwork. 
    
    Each session ends with a mini-challenge or game to boost engagement and reinforce learning. Perfect for curious minds eager to dive into tech, science, and innovation in a fun and structured way.
  `,
    learningOutcomes: [
      'Understand what robotics, electronics, and AI are and their real-world uses',
      'Build games and animations using block-based coding tools',
      'Explore basic electronics and simulate circuits online',
      'Apply STEM concepts through interactive DIY projects',
      'Strengthen communication, critical thinking, and teamwork',
      'Recognize the importance of ethics, safety, and responsible tech use',
      'Get introduced to basic AI and training models'
    ],
    curriculum: [
      {
        session: 1,
        title: 'Welcome to STEM!',
        topics: [
          'What is STEM?',
          'Introduction to Robotics, Electronics & AI',
          'Why STEM matters?',
          'Fun team-building activity'
        ]
      },
      {
        session: 2,
        title: 'Getting Started with Pictoblox/Scratch',
        topics: [
          'Block-based programming',
          'Creating your first animation',
          'Using flowcharts to plan ideas'
        ]
      },
      {
        session: 3,
        title: 'Code Your Own Game – Part 1',
        topics: [
          'Game design logic',
          'Player controls',
          'Sprites and backgrounds'
        ]
      },
      {
        session: 4,
        title: 'Code Your Own Game – Part 2',
        topics: [
          'Scoring system',
          'Win/Lose logic',
          'Finalizing the game'
        ]
      },
      {
        session: 5,
        title: 'Hands-On STEM: Engineering Challenge',
        topics: [
          'Build a strong bridge with paper',
          'Understand force and balance',
          'Work as a team'
        ]
      },
      {
        session: 6,
        title: 'Hands-On STEM: Creative Machines',
        topics: [
          'Make a basic gear mechanism',
          'Explore motion and energy'
        ]
      },
      {
        session: 7,
        title: 'Hands-On STEM: Science Lab',
        topics: [
          'Fun experiments with household materials',
          'Concepts: pressure, chemical reactions'
        ]
      },
      {
        session: 8,
        title: 'Intro to Electronics & Circuit Basics',
        topics: [
          'Voltage, current, resistance',
          'Simple circuits with LEDs and batteries'
        ]
      },
      {
        session: 9,
        title: 'Online Circuit Simulations & Troubleshooting',
        topics: [
          'Use tools like Tinkercad Circuits or Falstad',
          'Build & debug circuits virtually'
        ]
      },
      {
        session: 10,
        title: 'Essential Skills: Attitude, Teamwork & Ethics',
        topics: [
          'Importance of communication & empathy',
          'Responsible tech use',
          'Good ethics in STEM careers'
        ]
      },
      {
        session: 11,
        title: 'Intro to Artificial Intelligence',
        topics: [
          'What is AI?',
          'Image recognition game',
          'Train a model with examples'
        ]
      },
      {
        session: 12,
        title: 'STEM Master Challenge & Showcase',
        topics: [
          'Final creative challenge',
          'Mini project presentation',
          'Certificate & badge ceremony'
        ]
      }
    ],
    instructors: [
      {
        name: 'Nimashi Jayawardena',
        qualification: 'BSc in Computer Science, Certified STEM Educator',
        experience: '6+ years teaching coding and robotics to school children'
      }
    ],
    materials: [
      'Student STEM kit (basic components)',
      'Access to online tools (Scratch, Tinkercad, etc.)',
      'Daily worksheets and progress badges',
      'Certificate of Completion'
    ],
    testimonials: [
      {
        name: 'Tharushi M.',
        relation: 'Parent',
        comment: 'My son absolutely loved the hands-on learning style! He now talks about sensors, coding, and even AI at home!'
      },
      {
        name: 'Kevin A.',
        relation: 'Student (age 12)',
        comment: "It was the most fun class ever! I built a robot, made my own game, and showed it to my parents on the last day!"
      }
    ]
  },
  {
    id: 'robotics-beginners',
    title: 'Robotics for Beginners',
    shortDescription: 'Introduction to robotics concepts and basic programming for students new to STEM.',
    category: 'Robotics',
    categoryIcon: FaRobot,
    level: 'Beginner',
    duration: '8 weeks',
    ageGroup: '8-12 years',
    image: '/courses/robotics-beginners.jpg',
    price: 'LKR 15,000',
    availability: 'Available',
    rating: 4.8,
    studentsEnrolled: 250,
    fullDescription: `
      Our introductory robotics course is designed to spark curiosity and build foundational STEM skills.
      Students will learn basic robotics concepts, simple programming, and problem-solving through
      hands-on activities and engaging projects.
      
      This course is perfect for young learners who are just beginning their STEM journey and want to
      explore the exciting world of robotics in a fun, supportive environment.
    `,
    learningOutcomes: [
      'Understand basic robotics components and how they work together',
      'Learn simple programming concepts and logic',
      'Develop problem-solving skills through hands-on projects',
      'Build and operate simple robotic mechanisms',
      'Work collaboratively in a team environment'
    ],
    curriculum: [
      {
        week: 1,
        title: 'Introduction to Robotics',
        topics: ['What is a robot?', 'Basic components', 'Simple mechanics']
      },
      {
        week: 2,
        title: 'Moving Parts',
        topics: ['Motors and movement', 'Building a simple moving robot', 'Testing and improving designs']
      },
      {
        week: 3,
        title: 'Basic Programming',
        topics: ['Introduction to block programming', 'Sequential instructions', 'Making your robot move']
      },
      {
        week: 4,
        title: 'Sensors Introduction',
        topics: ['What are sensors?', 'Types of sensors', 'Adding a sensor to your robot']
      },
      {
        week: 5,
        title: 'Making Decisions',
        topics: ['If-then programming', 'Robot reactions to environment', 'Building a smarter robot']
      },
      {
        week: 6,
        title: 'Loops and Repetition',
        topics: ['Repeating actions', 'Efficiency in programming', 'Creating patterns of movement']
      },
      {
        week: 7,
        title: 'Mini Project Preparation',
        topics: ['Planning a robot project', 'Design and construction', 'Programming your creation']
      },
      {
        week: 8,
        title: 'Project Showcase',
        topics: ['Completing projects', 'Demonstrating your robot', 'Certificate ceremony']
      }
    ],
    instructors: [
      {
        name: 'Dasun Perera',
        qualification: 'BSc in Robotics Engineering',
        experience: '5+ years teaching robotics to children'
      }
    ],
    materials: [
      'Robotics kit (included in fee)',
      'Instruction manual',
      'Access to online resources',
      'Certificate of completion'
    ],
    testimonials: [
      {
        name: 'Priya M.',
        relation: 'Parent',
        comment: "My son was completely engaged throughout the course. The hands-on approach kept him interested and he's now eager to continue learning robotics!"
      },
      {
        name: 'Ravi K.',
        relation: 'Student (age 11)',
        comment: 'This was my favorite class ever! I built a robot that could follow lines and avoid obstacles. So cool!'
      }
    ]
  },
  {
    id: 'robotics-advanced',
    title: 'Advanced Robotics & AI',
    shortDescription: 'Advanced course for students ready to explore complex robotics challenges and artificial intelligence.',
    category: 'Robotics',
    categoryIcon: FaRobot,
    level: 'Advanced',
    duration: '12 weeks',
    ageGroup: '13-16 years',
    image: '/courses/robotics-advanced.jpg',
    price: 'LKR 25,000',
    availability: 'Limited Seats',
    rating: 4.9,
    studentsEnrolled: 120,
    fullDescription: `
      This advanced robotics course takes students to the next level, introducing complex programming
      concepts, artificial intelligence basics, and sophisticated mechanical designs. Students will work
      with advanced sensors, implement autonomous behaviors, and develop robots that can adapt to
      their environment.
      
      Ideal for students who have completed our beginner course or have equivalent experience in robotics
      and are ready for more challenging projects and concepts.
    `,
    learningOutcomes: [
      'Design and build complex robotic systems with multiple functions',
      'Program sophisticated behaviors using textual programming languages',
      'Implement basic AI concepts like decision trees and simple machine learning',
      'Understand and use advanced sensors and actuators',
      'Develop problem-solving approaches for real-world robotics challenges',
      'Complete a capstone project demonstrating advanced robotics concepts'
    ],
    curriculum: [
      {
        week: 1,
        title: 'Advanced Robotics Architecture',
        topics: ['Complex mechanical systems', 'Power and efficiency', 'Advanced components']
      },
      {
        week: 2,
        title: 'Programming in Python for Robotics',
        topics: ['Introduction to Python', 'Variables and functions', 'Robot control with code']
      },
      {
        week: 3,
        title: 'Advanced Sensor Integration',
        topics: ['Multiple sensor types', 'Data acquisition', 'Sensor fusion concepts']
      },
      {
        week: 4,
        title: 'Autonomous Navigation',
        topics: ['Path planning algorithms', 'Obstacle avoidance', 'Mapping environments']
      },
      {
        week: 5,
        title: 'Introduction to AI in Robotics',
        topics: ['What is AI?', 'Decision making algorithms', 'Pattern recognition']
      },
      {
        week: 6,
        title: 'Machine Learning Basics',
        topics: ['Training simple models', 'Supervised learning', 'Implementing ML in robots']
      },
      {
        week: 7,
        title: 'Advanced Motion Control',
        topics: ['Precision movement', 'Servo control', 'Complex action sequences']
      },
      {
        week: 8,
        title: 'Human-Robot Interaction',
        topics: ['Input/output interfaces', 'Voice control', 'Responsive behaviors']
      },
      {
        week: 9,
        title: 'Project Planning',
        topics: ['Capstone project introduction', 'Requirements analysis', 'Design planning']
      },
      {
        week: 10,
        title: 'Project Development I',
        topics: ['Building advanced mechanisms', 'Initial programming', 'Testing approaches']
      },
      {
        week: 11,
        title: 'Project Development II',
        topics: ['Refining systems', 'Debugging', 'Performance optimization']
      },
      {
        week: 12,
        title: 'Final Project Showcase',
        topics: ['Project demonstrations', 'Peer evaluation', 'Future directions in robotics']
      }
    ],
    instructors: [
      {
        name: 'Dr. Amara Jayawardena',
        qualification: 'PhD in Robotics and AI',
        experience: '10+ years in robotics research and education'
      },
      {
        name: 'Asitha Fernando',
        qualification: 'MSc in Computer Science',
        experience: 'Former robotics competition champion and industry professional'
      }
    ],
    materials: [
      'Advanced robotics kit (included in fee)',
      'Python programming resources',
      'Access to AI libraries and tools',
      'Online project portfolio',
      'Advanced certification'
    ],
    testimonials: [
      {
        name: 'Dinesh T.',
        relation: 'Parent',
        comment: "This course transformed my daughter's interest in technology into a passion. The advanced concepts were challenging but presented in a way she could understand and apply."
      },
      {
        name: 'Malisha S.',
        relation: 'Student (age 15)',
        comment: "I never thought I'd be able to program a robot that could recognize objects and make decisions on its own! This course opened my eyes to what's possible with AI and robotics."
      }
    ]
  },
  {
    id: 'coding-fundamentals',
    title: 'Coding Fundamentals',
    shortDescription: 'Learn the basics of programming with fun, interactive projects for beginners.',
    category: 'Programming',
    categoryIcon: FaCode,
    level: 'Beginner',
    duration: '10 weeks',
    ageGroup: '9-14 years',
    image: '/courses/coding-fundamentals.jpg',
    price: 'LKR 18,000',
    availability: 'Available',
    rating: 4.7,
    studentsEnrolled: 180,
    fullDescription: `
      This foundational course introduces young learners to the world of programming through interactive, 
      engaging activities and projects. Students will learn basic coding concepts while developing 
      logical thinking and problem-solving skills.
      
      Using block-based programming languages like Scratch, students will create their own animations,
      stories, and simple games while learning fundamental programming concepts that provide a solid
      foundation for more advanced coding.
    `,
    learningOutcomes: [
      'Understand fundamental programming concepts like sequences, loops, and conditionals',
      'Develop logical thinking and problem-solving skills',
      'Create interactive stories, animations, and simple games',
      'Learn to break down problems into smaller, manageable parts',
      'Build confidence in using technology creatively'
    ],
    curriculum: [
      {
        week: 1,
        title: 'Introduction to Programming',
        topics: ['What is coding?', 'Block-based programming', 'Creating your first program']
      },
      {
        week: 2,
        title: 'Sequences and Algorithms',
        topics: ['Step-by-step instructions', 'Building sequences', 'Simple animations']
      },
      {
        week: 3,
        title: 'Loops and Repetition',
        topics: ['Repeating actions', 'Efficient programming', 'Creating patterns']
      },
      {
        week: 4,
        title: 'Conditionals and Decisions',
        topics: ['If-then statements', 'Making choices in programs', 'Interactive stories']
      },
      {
        week: 5,
        title: 'Variables and Data',
        topics: ['Storing information', 'Changing values', 'Using variables in projects']
      },
      {
        week: 6,
        title: 'Events and Interactions',
        topics: ['Responding to user input', 'Keyboard controls', 'Mouse events']
      },
      {
        week: 7,
        title: 'Building a Simple Game I',
        topics: ['Game design basics', 'Creating characters', 'Adding challenges']
      },
      {
        week: 8,
        title: 'Building a Simple Game II',
        topics: ['Scoring systems', 'Levels and progression', 'Game testing']
      },
      {
        week: 9,
        title: 'Final Project Development',
        topics: ['Planning your project', 'Building and testing', 'Refining your code']
      },
      {
        week: 10,
        title: 'Project Showcase',
        topics: ['Presenting your creation', 'Peer feedback', 'Next steps in coding']
      }
    ],
    instructors: [
      {
        name: 'Chamari Gunawardena',
        qualification: 'BSc in Computer Science',
        experience: 'Specialized in teaching programming to young learners'
      }
    ],
    materials: [
      'Course handbook with coding exercises',
      'Access to online learning platform',
      'Project templates and resources',
      'Certificate of completion'
    ],
    testimonials: [
      {
        name: 'Anjali P.',
        relation: 'Parent',
        comment: 'My daughter loved creating her own stories and games. The course made coding accessible and fun - now she wants to learn more!'
      },
      {
        name: 'Kamal R.',
        relation: 'Student (age 12)',
        comment: 'I made a cool game where you have to catch falling stars. Coding is awesome and I want to make more games now!'
      }
    ]
  },
  {
    id: 'electronics-workshop',
    title: 'Electronics & Circuit Design',
    shortDescription: 'Hands-on exploration of electronics fundamentals through practical circuit building projects.',
    category: 'Electronics',
    categoryIcon: FaMicrochip,
    level: 'Intermediate',
    duration: '8 weeks',
    ageGroup: '12-16 years',
    image: '/courses/electronics-workshop.jpg',
    price: 'LKR 20,000',
    availability: 'Available',
    rating: 4.6,
    studentsEnrolled: 95,
    fullDescription: `
      This hands-on course introduces students to the fascinating world of electronics through
      practical circuit-building projects. Students will learn about electronic components,
      circuit design, and basic principles of electricity while creating their own working
      electronic devices.
      
      Perfect for curious minds who enjoy building things and want to understand how electronic
      devices work. Students will develop technical skills that form the foundation for more
      advanced electronics and engineering projects.
    `,
    learningOutcomes: [
      'Understand basic electronic components and their functions',
      'Read and create simple circuit diagrams',
      'Build working electronic circuits from scratch',
      'Apply principles of electricity to practical projects',
      'Troubleshoot and solve problems in electronic systems',
      'Complete a personal electronics project'
    ],
    curriculum: [
      {
        week: 1,
        title: 'Introduction to Electronics',
        topics: ['Basic electricity concepts', 'Electronic components overview', 'Safety practices']
      },
      {
        week: 2,
        title: 'Simple Circuits',
        topics: ['Circuit diagrams', 'Series and parallel circuits', 'Building your first LED circuit']
      },
      {
        week: 3,
        title: 'Resistors and Capacitors',
        topics: ['Understanding resistance', 'Storing electrical energy', 'Time-based circuits']
      },
      {
        week: 4,
        title: 'Transistors and Switches',
        topics: ['Controlling current flow', 'Amplification basics', 'Automatic switch circuits']
      },
      {
        week: 5,
        title: 'Sensors and Input',
        topics: ['Light sensors', 'Temperature detection', 'Motion sensors']
      },
      {
        week: 6,
        title: 'Integrated Circuits',
        topics: ['555 timer projects', 'Logic gates introduction', 'Sound generating circuits']
      },
      {
        week: 7,
        title: 'Project Development',
        topics: ['Personal project planning', 'Circuit design', 'Component selection']
      },
      {
        week: 8,
        title: 'Project Completion',
        topics: ['Finalizing builds', 'Testing and troubleshooting', 'Project demonstrations']
      }
    ],
    instructors: [
      {
        name: 'Sunil Rathnayake',
        qualification: 'Electrical Engineering Diploma',
        experience: '8 years teaching electronics to students'
      }
    ],
    materials: [
      'Electronics component kit (included in fee)',
      'Digital multimeter for testing',
      'Circuit design workbook',
      'Online access to circuit simulators',
      'Certificate of completion'
    ],
    testimonials: [
      {
        name: 'Lasith J.',
        relation: 'Parent',
        comment: 'The hands-on approach to learning electronics was perfect for my son. He now understands the principles behind the devices he uses every day.'
      },
      {
        name: 'Tharushi W.',
        relation: 'Student (age 14)',
        comment: 'I built an alarm system for my room that detects when someone enters! Understanding how circuits work has made me interested in studying engineering.'
      }
    ]
  },
  {
    id: 'science-explorers',
    title: 'Science Explorers Lab',
    shortDescription: 'Exciting hands-on science experiments that make learning physics, chemistry, and biology concepts fun.',
    category: 'Science',
    categoryIcon: FaFlask,
    level: 'Beginner to Intermediate',
    duration: '8 weeks',
    ageGroup: '8-13 years',
    image: '/courses/science-explorers.jpg',
    price: 'LKR 16,000',
    availability: 'Available',
    rating: 4.8,
    studentsEnrolled: 210,
    fullDescription: `
      Science Explorers Lab turns students into real scientists through hands-on experiments and
      investigations. This course makes complex scientific concepts accessible and exciting by
      allowing students to discover principles of physics, chemistry, and biology through
      direct experimentation.
      
      Each session focuses on different scientific disciplines with age-appropriate experiments
      that encourage critical thinking, hypothesis testing, and careful observation - all key
      skills in the scientific method and STEM fields.
    `,
    learningOutcomes: [
      'Understand fundamental concepts in physics, chemistry, and biology',
      'Apply the scientific method to investigate questions',
      'Develop observation and data collection skills',
      'Make predictions and test hypotheses',
      'Connect scientific principles to everyday phenomena',
      'Communicate findings clearly through simple lab reports'
    ],
    curriculum: [
      {
        week: 1,
        title: 'Scientific Method Adventure',
        topics: ['Steps of scientific investigation', 'Observation skills', 'First experiments']
      },
      {
        week: 2,
        title: 'Physics in Action',
        topics: ['Forces and motion', 'Simple machines', 'Energy transformations']
      },
      {
        week: 3,
        title: 'Chemistry Magic',
        topics: ['States of matter', 'Safe chemical reactions', 'Acid and base discoveries']
      },
      {
        week: 4,
        title: 'Biology Exploration',
        topics: ['Cells and microscopy', 'Plant experiments', 'Ecosystems in miniature']
      },
      {
        week: 5,
        title: 'Engineering Challenges',
        topics: ['Building structures', 'Testing materials', 'Design competitions']
      },
      {
        week: 6,
        title: 'Earth and Environmental Science',
        topics: ['Water cycle', 'Weather experiments', 'Erosion and geology']
      },
      {
        week: 7,
        title: 'Science of Sound and Light',
        topics: ['Sound waves', 'Light properties', 'Optical illusions']
      },
      {
        week: 8,
        title: 'Science Fair Preparation',
        topics: ['Choosing projects', 'Conducting experiments', 'Presenting findings']
      }
    ],
    instructors: [
      {
        name: 'Nadeeka Jayasuriya',
        qualification: 'MSc in Science Education',
        experience: 'Former science teacher with specialty in hands-on learning'
      }
    ],
    materials: [
      'Science experiment kit (included in fee)',
      'Lab notebook',
      'Safety equipment',
      'Take-home experiment guides',
      'Science explorer certificate'
    ],
    testimonials: [
      {
        name: 'Dilshan M.',
        relation: 'Parent',
        comment: 'My daughter comes home excited to share what she learned each week. The experiments are educational but also incredibly fun!'
      },
      {
        name: 'Ayesha P.',
        relation: 'Student (age 10)',
        comment: 'I made slime and learned about polymers, built a volcano, and saw how plants drink water with colored dye. Science is amazing!'
      }
    ]
  },
  {
    id: 'game-development',
    title: 'Game Development',
    shortDescription: 'Learn to design and create your own video games using professional development tools.',
    category: 'Programming',
    categoryIcon: FaGamepad,
    level: 'Intermediate',
    duration: '12 weeks',
    ageGroup: '13-18 years',
    image: '/courses/game-development.jpg',
    price: 'LKR 22,000',
    availability: 'Limited Seats',
    rating: 4.9,
    studentsEnrolled: 75,
    fullDescription: `
      This course introduces students to the exciting world of game development, combining coding skills
      with creativity and design thinking. Students will learn to use industry-standard game development
      tools to create their own playable games from concept to completion.
      
      Perfect for students with some coding experience who want to apply their skills to build interactive
      games while learning about game mechanics, user experience, and the principles that make games
      engaging and fun.
    `,
    learningOutcomes: [
      'Understand core game development concepts and terminology',
      'Design game mechanics and implement them in code',
      'Create and integrate visual and audio elements',
      'Build fully functioning 2D games with multiple levels',
      'Apply math and physics concepts to game situations',
      'Test and refine games based on user feedback',
      'Complete a polished game development portfolio'
    ],
    curriculum: [
      {
        week: 1,
        title: 'Game Development Foundations',
        topics: ['Game engines overview', 'Setting up development environment', 'Creating your first scene']
      },
      {
        week: 2,
        title: 'Game Design Principles',
        topics: ['Core gameplay loops', 'Player experience', 'Balancing challenge and reward']
      },
      {
        week: 3,
        title: 'Character Movement',
        topics: ['Programming player controls', 'Physics and collisions', 'Character animation basics']
      },
      {
        week: 4,
        title: 'Game Objects and Interactions',
        topics: ['Creating interactive elements', 'Collectibles and power-ups', 'Obstacle implementation']
      },
      {
        week: 5,
        title: 'User Interface Design',
        topics: ['Menus and navigation', 'HUD elements', 'Score and health systems']
      },
      {
        week: 6,
        title: 'Enemy AI and Challenges',
        topics: ['Basic artificial intelligence', 'Enemy behaviors', 'Difficulty progression']
      },
      {
        week: 7,
        title: 'Level Design',
        topics: ['Creating engaging environments', 'Level flow', 'Building multiple stages']
      },
      {
        week: 8,
        title: 'Audio Integration',
        topics: ['Sound effects', 'Background music', 'Audio cues and feedback']
      },
      {
        week: 9,
        title: 'Game States and Management',
        topics: ['Win/lose conditions', 'Scene transitions', 'Saving game progress']
      },
      {
        week: 10,
        title: 'Polishing and Effects',
        topics: ['Visual effects', 'Game feel improvements', 'Performance optimization']
      },
      {
        week: 11,
        title: 'Testing and Refinement',
        topics: ['Playtesting methods', 'Debugging common issues', 'Implementing feedback']
      },
      {
        week: 12,
        title: 'Publishing and Showcase',
        topics: ['Building for different platforms', 'Creating game trailers', 'Project presentations']
      }
    ],
    instructors: [
      {
        name: 'Rohan De Silva',
        qualification: 'BSc in Game Development',
        experience: 'Independent game developer with published titles'
      }
    ],
    materials: [
      'Game development software licenses (for course duration)',
      'Digital art and audio resources package',
      'Game design document templates',
      'Access to online learning platform',
      'Digital portfolio showcase'
    ],
    testimonials: [
      {
        name: 'Michelle F.',
        relation: 'Parent',
        comment: "My son was already interested in games, but this course channeled that interest into creative development skills. He's now considering game development as a career path."
      },
      {
        name: 'Vikram S.',
        relation: 'Student (age 16)',
        comment: "I created a complete 2D platformer game with multiple levels! The course taught me both technical skills and how to make a game that's actually fun to play."
      }
    ]
  },
  {
    id: 'math-problem-solving',
    title: 'Mathematics through Problem Solving',
    shortDescription: 'Strengthen mathematical thinking through creative problem-solving approaches and real-world applications.',
    category: 'Mathematics',
    categoryIcon: FaChalkboardTeacher,
    level: 'Intermediate',
    duration: '10 weeks',
    ageGroup: '10-15 years',
    image: '/courses/math-problem-solving.jpg',
    price: 'LKR 15,000',
    availability: 'Available',
    rating: 4.7,
    studentsEnrolled: 110,
    fullDescription: `
      This unique mathematics course moves beyond standard calculations to develop deeper mathematical
      thinking through creative problem-solving. Students will tackle engaging challenges that require
      them to apply mathematical concepts in novel ways, developing flexible thinking and confidence
      in their mathematical abilities.
      
      Rather than focusing on rote learning, the course emphasizes multiple solution pathways,
      pattern recognition, and logical reasoning - essential skills for success in all STEM fields
      and mathematical competitions.
    `,
    learningOutcomes: [
      'Develop creative approaches to mathematical problem-solving',
      'Strengthen logical reasoning and pattern recognition',
      'Apply mathematical concepts to real-world situations',
      'Build confidence in tackling unfamiliar problems',
      'Improve ability to communicate mathematical ideas',
      'Prepare for mathematics competitions and advanced courses'
    ],
    curriculum: [
      {
        week: 1,
        title: 'Mathematical Thinking',
        topics: ['Problem-solving strategies', 'Multiple solution pathways', 'Pattern recognition']
      },
      {
        week: 2,
        title: 'Number Puzzles and Patterns',
        topics: ['Number theory challenges', 'Mathematical games', 'Sequence problems']
      },
      {
        week: 3,
        title: 'Geometric Reasoning',
        topics: ['Spatial puzzles', 'Geometric construction challenges', 'Visual proofs']
      },
      {
        week: 4,
        title: 'Logic and Deduction',
        topics: ['Logical puzzles', 'Truth tables', 'Deductive reasoning challenges']
      },
      {
        week: 5,
        title: 'Combinatorial Thinking',
        topics: ['Counting principles', 'Permutation puzzles', 'Probability problems']
      },
      {
        week: 6,
        title: 'Algebra through Problem Solving',
        topics: ['Word problems', 'Equation strategies', 'Functional relationships']
      },
      {
        week: 7,
        title: 'Mathematical Modeling',
        topics: ['Real-world applications', 'Creating mathematical models', 'Optimization problems']
      },
      {
        week: 8,
        title: 'Data Analysis and Patterns',
        topics: ['Statistical reasoning', 'Data interpretation challenges', 'Trend analysis']
      },
      {
        week: 9,
        title: 'Competition Preparation',
        topics: ['Time management strategies', 'Common competition problem types', 'Practice tests']
      },
      {
        week: 10,
        title: 'Mathematics Festival',
        topics: ['Team challenges', 'Math relay races', 'Problem-solving showcase']
      }
    ],
    instructors: [
      {
        name: 'Dr. Rajitha Mendis',
        qualification: 'PhD in Mathematics',
        experience: 'Mathematics competition coach with national winners'
      }
    ],
    materials: [
      'Problem-solving workbook',
      'Mathematical puzzle set',
      'Access to online problem database',
      'Competition preparation resources',
      'Course completion certificate'
    ],
    testimonials: [
      {
        name: 'Harini L.',
        relation: 'Parent',
        comment: "This course completely changed my daughter's relationship with mathematics. She now sees math as a creative subject full of interesting challenges rather than just calculations."
      },
      {
        name: 'Nisal J.',
        relation: 'Student (age 13)',
        comment: "I used to think math was boring, but now I love solving tricky problems! I won third place in my school's math competition after taking this course."
      }
    ]
  }
];

export default coursesData; 